/**
 * Filter Bar Component for Calendar
 */

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { CalendarIcon, List } from 'lucide-react';
import { CalendarEvent, SupplierProfile } from '../../lib/api';
import { CalendarViewType, ViewMode, EventPeriod } from './types';
import { EVENT_TYPE_COLORS, EVENT_TYPE_LABELS } from './constants';

interface FilterBarProps {
  viewMode: ViewMode;
  calendarView: CalendarViewType;
  events: CalendarEvent[];
  suppliers: SupplierProfile[];
  activeEventTypes: string[];
  selectedSupplier: string;
  eventPeriod: EventPeriod;
  onViewModeChange: (mode: ViewMode) => void;
  onCalendarViewChange: (view: CalendarViewType) => void;
  onSupplierChange: (supplier: string) => void;
  onEventPeriodChange: (period: EventPeriod) => void;
  onEventTypeToggle: (type: string) => void;
}

export function FilterBar({
  viewMode,
  calendarView,
  events,
  suppliers,
  activeEventTypes,
  selectedSupplier,
  eventPeriod,
  onViewModeChange,
  onCalendarViewChange,
  onSupplierChange,
  onEventPeriodChange,
  onEventTypeToggle,
}: FilterBarProps) {
  return (
    <div className="flex flex-col gap-4">
      {/* Top row: View Mode Toggle */}
      <div className="flex justify-between items-center">
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('calendar')}
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            Calendar
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('list')}
          >
            <List className="h-4 w-4 mr-2" />
            List
          </Button>
        </div>

        {/* Calendar View Controls (only show in calendar mode) */}
        {viewMode === 'calendar' && (
          <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
            <Button
              variant={calendarView === 'day' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onCalendarViewChange('day')}
            >
              Day
            </Button>
            <Button
              variant={calendarView === 'week' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onCalendarViewChange('week')}
            >
              Week
            </Button>
            <Button
              variant={calendarView === 'month' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onCalendarViewChange('month')}
            >
              Month
            </Button>
          </div>
        )}
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Supplier Filter */}
        <Select value={selectedSupplier} onValueChange={onSupplierChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Suppliers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Suppliers</SelectItem>
            {suppliers.map((supplier) => (
              <SelectItem key={supplier.id} value={supplier.company.toString()}>
                {supplier.company_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Period Filter */}
        <Select value={eventPeriod} onValueChange={(value) => onEventPeriodChange(value as EventPeriod)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Upcoming" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="upcoming">Upcoming</SelectItem>
            <SelectItem value="last_3_months">Last 3 Months</SelectItem>
            <SelectItem value="last_6_months">Last 6 Months</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>

        {/* Event Type Filter - Compact Badges */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(EVENT_TYPE_LABELS).map(([type, label]) => {
            const count = events.filter((e) => e.event_type === type).length;
            if (count === 0) return null;
            const isActive = activeEventTypes.includes(type);
            return (
              <Badge
                key={type}
                variant={isActive ? 'default' : 'outline'}
                className={`cursor-pointer hover:opacity-80 transition-opacity ${
                  isActive ? '' : 'bg-secondary'
                }`}
                onClick={() => onEventTypeToggle(type)}
              >
                <div className={`w-2 h-2 rounded-full mr-1.5 ${EVENT_TYPE_COLORS[type]}`} />
                {label}
              </Badge>
            );
          })}
        </div>
      </div>
    </div>
  );
}
